import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Landmark, RefreshCw, Play, Search, AlertTriangle, BarChart3 } from "lucide-react";
import ModuleHeader from "@/components/ModuleHeader";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

function fmtNum(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
function fmtBRL(v: number | string | null | undefined) {
  if (v == null || v === "") return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

const TIPOS: Record<string, string> = {
  FGTS: "FGTS",
  PREVIDENCIARIO: "Previdenciário",
  NAO_PREVIDENCIARIO: "Não-Previdenciário",
};

function ImportCard({ status }: { status: any }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showUf, setShowUf] = useState(false);

  const importAllMut = useMutation({
    mutationFn: () => authFetch("/api/pgfn/importar", { method: "POST" }),
    onSuccess: (d: any) => {
      toast({ title: "Importação iniciada", description: d.message });
      qc.invalidateQueries({ queryKey: ["/api/pgfn/status"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const importTipoMuts: Record<string, any> = {
    FGTS: useMutation({
      mutationFn: () => authFetch("/api/pgfn/importar/FGTS", { method: "POST" }),
      onSuccess: (d: any) => { toast({ title: "FGTS iniciado", description: d.message }); qc.invalidateQueries({ queryKey: ["/api/pgfn/status"] }); },
      onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
    }),
    PREVIDENCIARIO: useMutation({
      mutationFn: () => authFetch("/api/pgfn/importar/PREVIDENCIARIO", { method: "POST" }),
      onSuccess: (d: any) => { toast({ title: "Previdenciário iniciado", description: d.message }); qc.invalidateQueries({ queryKey: ["/api/pgfn/status"] }); },
      onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
    }),
    NAO_PREVIDENCIARIO: useMutation({
      mutationFn: () => authFetch("/api/pgfn/importar/NAO_PREVIDENCIARIO", { method: "POST" }),
      onSuccess: (d: any) => { toast({ title: "Não-Prev. iniciado", description: d.message }); qc.invalidateQueries({ queryKey: ["/api/pgfn/status"] }); },
      onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
    }),
  };

  const syncAtivo = status?.sync_ativo;
  const progresso = status?.sync_progresso || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Importação</span>
          <Button
            size="sm" variant="outline"
            onClick={() => qc.invalidateQueries({ queryKey: ["/api/pgfn/status"] })}
            data-testid="button-refresh-pgfn"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Fonte: <strong>dadosabertos.pgfn.gov.br</strong> — 3 ZIPs nacionais (FGTS, Previdenciário, Não-Previdenciário).
          Cron trimestral automático: jan/abr/jul/out, dia 5, às 03h.
        </p>

        {syncAtivo && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="animate-pulse">Importando {status.sync_tipo_atual || "…"}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{status.sync_log}</p>
          </div>
        )}

        {status?.sync_erro && (
          <p className="text-sm text-destructive">Último erro: {status.sync_erro}</p>
        )}

        {status?.sync_resultados && Object.keys(status.sync_resultados).length > 0 && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p className="font-medium">Último resultado:</p>
            {Object.entries(status.sync_resultados).map(([tipo, r]: [string, any]) => (
              <p key={tipo}>• {TIPOS[tipo] || tipo}: {fmtNum(r.inseridos)} inseridos, {fmtNum(r.atualizados)} atualizados {r.erro ? `❌ ${r.erro}` : "✓"}</p>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <Button
            className="w-full"
            onClick={() => importAllMut.mutate()}
            disabled={importAllMut.isPending || syncAtivo}
            data-testid="button-importar-pgfn-todos"
          >
            <Play className="h-4 w-4 mr-1" />
            {syncAtivo ? "Importando…" : "Importar Todos os Tipos (FGTS + Prev + Não-Prev)"}
          </Button>

          <div className="grid grid-cols-3 gap-2">
            {Object.entries(TIPOS).map(([key, label]) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                onClick={() => importTipoMuts[key].mutate()}
                disabled={importTipoMuts[key].isPending || syncAtivo}
                data-testid={`button-importar-pgfn-${key.toLowerCase()}`}
              >
                <Play className="h-3 w-3 mr-1" /> {label}
              </Button>
            ))}
          </div>
        </div>

        {status?.ultimas_importacoes?.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Histórico</p>
            {status.ultimas_importacoes.map((r: any, i: number) => (
              <div key={i} className="text-xs flex gap-3 border-b pb-1 last:border-0">
                <span className="font-mono text-muted-foreground">{fmtDate(r.criado_em)}</span>
                <Badge variant="outline" className="text-xs">{TIPOS[r.tipo_divida] || r.tipo_divida}</Badge>
                <span>{fmtNum(r.inseridos)} ins. / {fmtNum(r.atualizados)} atz.</span>
                {r.erro && <span className="text-destructive truncate">{r.erro}</span>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConsultaCnpj() {
  const [cnpjInput, setCnpjInput] = useState("");
  const [cnpjBusca, setCnpjBusca] = useState("");

  const q = useQuery({
    queryKey: ["/api/pgfn/devedor", cnpjBusca],
    queryFn: () => authFetch(`/api/pgfn/devedor/${cnpjBusca}`),
    enabled: !!cnpjBusca,
  });

  const data = q.data as any;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Search className="h-4 w-4" /> Consultar Devedor por CNPJ/CPF
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Label className="text-xs mb-1">CNPJ ou CPF (somente números)</Label>
            <Input
              placeholder="Ex: 12345678000190"
              value={cnpjInput}
              onChange={(e) => setCnpjInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setCnpjBusca(cnpjInput.replace(/\D/g, "")); }}
              data-testid="input-cnpj-pgfn"
            />
          </div>
          <Button
            className="self-end"
            onClick={() => setCnpjBusca(cnpjInput.replace(/\D/g, ""))}
            disabled={!cnpjInput.trim()}
            data-testid="button-buscar-pgfn"
          >
            <Search className="h-4 w-4 mr-1" /> Buscar
          </Button>
        </div>

        {q.isLoading && <Skeleton className="h-32 w-full" />}

        {data && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${data.has_divida_ativa ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800" : "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"}`}>
              <div className="flex items-center gap-2 mb-2">
                {data.has_divida_ativa
                  ? <AlertTriangle className="h-5 w-5 text-red-600" />
                  : <Landmark className="h-5 w-5 text-green-600" />}
                <span className="font-semibold">
                  {data.has_divida_ativa ? "⚠️ Possui Dívida Ativa da União" : "✓ Sem Dívida Ativa encontrada"}
                </span>
              </div>
              {data.has_divida_ativa && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground text-xs">Total de inscrições</span>
                    <div className="font-bold">{fmtNum(data.total_dividas)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Valor total consolidado</span>
                    <div className="font-bold">{fmtBRL(data.valor_total_consolidado)}</div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground text-xs">Tipos de dívida</span>
                    <div className="flex gap-1 flex-wrap mt-0.5">
                      {(data.por_tipo || []).map((t: any) => (
                        <Badge key={t.tipo} variant="destructive" className="text-xs">{TIPOS[t.tipo] || t.tipo} ({fmtNum(t.total)})</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {data.rows?.length > 0 && (
              <div className="overflow-x-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nº Inscrição</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.slice(0, 30).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs max-w-[160px] truncate">{r.nome_devedor || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{TIPOS[r.tipo_divida] || r.tipo_divida || "—"}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.numero_inscricao || "—"}</TableCell>
                        <TableCell className="text-xs">{r.situacao_inscricao || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtBRL(r.valor_consolidado)}</TableCell>
                        <TableCell className="text-xs">{r.data_inscricao || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {data.rows.length > 30 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    Exibindo 30 de {fmtNum(data.total_dividas)} inscrições
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PgfnPage() {
  const statusQ = useQuery({
    queryKey: ["/api/pgfn/status"],
    queryFn: () => authFetch("/api/pgfn/status"),
    refetchInterval: 6000,
  });

  const status = statusQ.data as any;
  const porTipo = status?.por_tipo || {};
  const porUf = status?.por_uf || {};
  const syncAtivo = status?.sync_ativo;

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={Landmark}
        iconColor="text-blue-700"
        title="PGFN — Dívida Ativa da União"
        description="Inscrições na Dívida Ativa da União (Procuradoria-Geral da Fazenda Nacional)"
        recordCount={status?.total}
        recordLabel="inscrições"
        isLoading={statusQ.isLoading}
        docsHref="/docs"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="h-4 w-4 text-blue-700" />
              <span className="text-xs text-muted-foreground">Total de Inscrições</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {statusQ.isLoading ? <Skeleton className="h-7 w-24" /> : fmtNum(status?.total)}
            </div>
          </CardContent>
        </Card>

        {Object.entries(TIPOS).map(([key, label]) => (
          <Card key={key}>
            <CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground mb-1">{label}</div>
              <div className="text-xl font-bold">
                {statusQ.isLoading ? <Skeleton className="h-7 w-16" /> : fmtNum(porTipo[key] ?? 0)}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Status</span>
            </div>
            {syncAtivo ? (
              <Badge variant="secondary" className="animate-pulse">Importando…</Badge>
            ) : (
              <Badge variant="outline">Ocioso</Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {Object.keys(porUf).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Por UF (top 27)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {Object.entries(porUf)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                .slice(0, 27)
                .map(([uf, total]) => (
                  <div key={uf} className="flex items-center justify-between border rounded px-2 py-1">
                    <Badge variant="outline" className="text-xs">{uf}</Badge>
                    <span className="text-xs font-mono">{fmtNum(total as number)}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      <ImportCard status={status} />
      <ConsultaCnpj />
    </div>
  );
}
